/**
 * Utility functions for managing localStorage and sessionStorage
 * Helps prevent conflicts with old data
 */

export const STORAGE_KEYS = {
  OUTLOOK_STORE: 'outlook-store-persist',
  AUTH_STORE: 'outlook-addin-auth-store',
  THEME_STORE: 'outlook-addin-theme-store',
  RESOURCE_STORE: 'resource-store',
  ATTENDEES_STORE: 'attendees-store',
  TOAST_STORE: 'toast-store',
  AGENT_STORE: 'agent-store',
} as const;

/**
 * Clear all old storage data that might cause conflicts
 */
export const clearOldStorageData = (): void => {
  console.log('🧹 Clearing old storage data...');
  
  const keysToRemove = Object.values(STORAGE_KEYS);
  let clearedCount = 0;

  // Clear localStorage
  keysToRemove.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`🗑️ Removing localStorage key: ${key}`);
      localStorage.removeItem(key);
      clearedCount++;
    }
  });

  // Clear sessionStorage
  keysToRemove.forEach(key => {
    if (sessionStorage.getItem(key)) {
      console.log(`🗑️ Removing sessionStorage key: ${key}`);
      sessionStorage.removeItem(key);
      clearedCount++;
    }
  });

  console.log(`✅ Cleared ${clearedCount} old storage entries`);
};

/**
 * Check if old storage data exists
 */
export const hasOldStorageData = (): boolean => {
  const keysToCheck = Object.values(STORAGE_KEYS);
  
  return keysToCheck.some(key => 
    localStorage.getItem(key) !== null || 
    sessionStorage.getItem(key) !== null
  );
};

/**
 * Get storage info for debugging
 */
export const getStorageInfo = (): { localStorage: string[], sessionStorage: string[] } => {
  const keysToCheck = Object.values(STORAGE_KEYS);
  
  const localStorageKeys = keysToCheck.filter(key => localStorage.getItem(key) !== null);
  const sessionStorageKeys = keysToCheck.filter(key => sessionStorage.getItem(key) !== null);
  
  return {
    localStorage: localStorageKeys,
    sessionStorage: sessionStorageKeys
  };
};

/**
 * Initialize storage cleanup on app start
 * Call this in your main component
 */
export const initializeStorageCleanup = (): void => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  if (isDevelopment && hasOldStorageData()) {
    console.warn('⚠️ Old storage data detected. Consider clearing it to avoid conflicts.');
    console.log('Storage info:', getStorageInfo());
    
    // Uncomment the line below to auto-clear old data in development
    // clearOldStorageData();
  }
};
